@let mandateObj = mandate();

@if (mandateObj) {
  <div class="flex flex-col gap-6">
    <!-- 1. MANDATE TYPE HIGHLIGHT BANNER -->
    @if (mandateTypeInfo(); as typeInfo) {
      <div
        class="p-5 rounded-2xl border border-primary/20 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gray-1"
      >
        <div class="flex items-start gap-3.5">
          <div
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-white shadow-xs"
          >
            <mat-icon class="text-[22px]! w-5.5! h-5.5! flex items-center justify-center text-white!"
              >handshake</mat-icon
            >
          </div>
          <div>
            <div class="flex items-center gap-2.5 flex-wrap">
              <h4 class="text-base font-bold text-gray-12 m-0">{{ typeInfo.title }}</h4>
              @if (typeInfo.badge) {
                <span
                  class="rounded-full border px-2.5 py-0.5 text-[11px] font-semibold {{
                    typeInfo.badgeColorClass
                  }}"
                >
                  {{ typeInfo.badge }}
                </span>
              }
            </div>
            <p class="mt-1 text-xs text-gray-11 leading-relaxed max-w-2xl">
              {{ typeInfo.description }}
            </p>
          </div>
        </div>

        <!-- Key benefits and drawbacks pill tags -->
        <div
          class="flex flex-col gap-1.5 border-t md:border-t-0 md:border-l border-primary/20 pt-3 md:pt-0 md:pl-5 shrink-0"
        >
          @for (benefit of typeInfo.benefits; track benefit) {
            <div class="flex items-center gap-1.5 text-xs text-gray-12 font-medium">
              <mat-icon class="text-primary text-[16px]! w-4! h-4! flex items-center justify-center"
                >check_circle</mat-icon
              >
              <span>{{ benefit }}</span>
            </div>
          }
          @if (typeInfo.drawbacks?.length) {
            @for (drawback of typeInfo.drawbacks; track drawback) {
              <div class="flex items-center gap-1.5 text-xs text-amber-11 font-medium">
                <mat-icon class="text-amber-10 text-[16px]! w-4! h-4! flex items-center justify-center"
                  >remove_circle_outline</mat-icon
                >
                <span>{{ drawback }}</span>
              </div>
            }
          }
        </div>
      </div>
    }

    <!-- 2. KEY METRICS: DURATION, COMMISSION, FAI & KEYS -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <!-- Durée -->
      <div class="rounded-2xl border border-gray-6 bg-gray-1 p-4 flex items-center gap-3.5">
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-a3 text-indigo-11"
        >
          <mat-icon class="text-[20px]! w-5! h-5! flex items-center justify-center text-indigo-11!"
            >calendar_today</mat-icon
          >
        </div>
        <div>
          <span class="text-[11px] font-bold uppercase tracking-wider text-gray-10 block"
            >Durée souhaitée</span
          >
          <span class="text-sm font-bold text-gray-12">
            {{ durationInfo()?.label || '3 mois' }}
          </span>
          <span class="text-[11px] text-gray-11 block">{{ durationInfo()?.hint }}</span>
        </div>
      </div>

      <!-- Taux d'honoraires max -->
      <div class="rounded-2xl border border-gray-6 bg-gray-1 p-4 flex items-center gap-3.5">
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-a3 text-indigo-11"
        >
          <mat-icon class="text-[20px]! w-5! h-5! flex items-center justify-center text-indigo-11!"
            >percent</mat-icon
          >
        </div>
        <div class="flex-1 min-w-0">
          <span class="text-[11px] font-bold uppercase tracking-wider text-gray-10 block"
            >Honoraires ciblés</span
          >
          <div class="flex items-center gap-2">
            <span class="text-sm font-bold text-primary">
              {{
                mandateObj.targetCommissionRate
                  ? (mandateObj.targetCommissionRate | number: '1.1-2' : 'fr-FR') + ' % max'
                  : 'À discuter'
              }}
            </span>
          </div>
          @if (sellingPriceEuros() && estimatedCommissionEuros()) {
            <span class="text-[11px] text-gray-11 block truncate">
              ~{{ estimatedCommissionEuros() | number: '1.0-0' : 'fr-FR' }} € pour
              {{ sellingPriceEuros() | number: '1.0-0' : 'fr-FR' }} € net
            </span>
          } @else {
            <span class="text-[11px] text-gray-11 block">Moyenne marché 3.5 % à 4.5 %</span>
          }
        </div>
      </div>

      <!-- Estimation FAI si prix présent -->
      <div class="rounded-2xl border border-gray-6 bg-gray-1 p-4 flex items-center gap-3.5">
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-a3 text-indigo-11"
        >
          <mat-icon class="text-[20px]! w-5! h-5! flex items-center justify-center text-indigo-11!"
            >payments</mat-icon
          >
        </div>
        <div>
          <div class="flex items-center gap-1">
            <span
              class="text-[11px] font-bold uppercase tracking-wider text-gray-10 block cursor-help"
              matTooltip="Budget total estimé"
              matTooltipPosition="above"
              >FAI</span
            >
            <div>
              <mat-icon
                class="text-xs! w-3! h-3! text-gray-9 hover:text-indigo-11 cursor-help"
                matTooltip="Budget total estimé"
                matTooltipPosition="above"
              >
                info
              </mat-icon>
            </div>
          </div>
          @if (sellingPriceEuros() && estimatedCommissionEuros()) {
            <span class="text-sm font-bold text-gray-12">
              {{ sellingPriceEuros()! + estimatedCommissionEuros()! | number: '1.0-0' : 'fr-FR' }} €
            </span>
            <span class="text-[11px] text-jade-11 font-medium block">Prix honoraires inclus</span>
          } @else {
            <span class="text-sm font-bold text-gray-12">Prix net + honoraires</span>
            <span class="text-[11px] text-gray-11 block">Défini selon l'offre finale</span>
          }
        </div>
      </div>
    </div>

    <!-- 3. ACCÈS & ORGANISATION DES VISITES -->
    @if (keyManagementList().length > 0) {
      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-2 text-gray-11">
          <mat-icon class="text-sm">key</mat-icon>
          <div class="flex items-center gap-1.5">
            <h4 class="font-bold uppercase tracking-wider text-xs m-0">
              Accès & Organisation des visites acquéreurs
            </h4>
            <mat-icon
              class="text-xs! w-3.5! h-3.5! text-gray-9 hover:text-primary cursor-help"
              matTooltip="Conseil pro : L'absence du propriétaire favorise la vente car les acquéreurs se projettent plus librement et osent exprimer leurs remarques sans crainte de froisser."
              matTooltipPosition="above"
            >
              info
            </mat-icon>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          @for (km of keyManagementList(); track km.label) {
            <div class="rounded-xl border border-gray-6 bg-gray-1 p-3.5 flex items-start gap-3">
              <div
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-a3 text-indigo-11"
              >
                <mat-icon class="text-[18px]! w-4.5! h-4.5! flex items-center justify-center text-indigo-11!">
                  {{ km.icon }}
                </mat-icon>
              </div>
              <div class="min-w-0">
                <span class="text-xs font-bold text-gray-12 block">{{ km.label }}</span>
                <span class="text-[11px] text-gray-11 leading-snug block mt-0.5">{{ km.description }}</span>
              </div>
            </div>
          }
        </div>
      </div>
    }

    <!-- 4. PRIORITÉS DU VENDEUR -->
    @if (prioritiesList().length > 0) {
      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-2 text-gray-11">
          <mat-icon class="text-sm">star_outline</mat-icon>
          <h4 class="font-bold uppercase tracking-wider text-xs m-0">
            Ce qui compte le plus pour le vendeur
          </h4>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          @for (priority of prioritiesList(); track priority.label) {
            <div class="rounded-xl border border-gray-6 bg-gray-1 p-3.5 flex items-start gap-3">
              <div
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-a3 text-indigo-11"
              >
                <mat-icon
                  class="text-[18px]! w-4.5! h-4.5! flex items-center justify-center text-indigo-11!"
                  >{{ priority.icon }}</mat-icon
                >
              </div>
              <div class="min-w-0">
                <span class="text-xs font-bold text-gray-12 block">{{ priority.label }}</span>
                <span class="text-[11px] text-gray-11 leading-snug block mt-0.5">{{
                  priority.description
                }}</span>
              </div>
            </div>
          }
        </div>
      </div>
    }

    <!-- 5. CONSIGNES & INSTRUCTIONS SPÉCIFIQUES -->
    @if (mandateObj.specialInstructions?.trim()) {
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2 text-gray-11">
          <mat-icon class="text-sm">comment</mat-icon>
          <h4 class="font-bold uppercase tracking-wider text-xs m-0">Consignes & attentes pour les agents</h4>
        </div>

        <div
          class="p-4 rounded-2xl border border-indigo-a4 bg-indigo-a2 text-xs text-gray-12 leading-relaxed"
        >
          <p class="m-0 italic whitespace-pre-line">« {{ mandateObj.specialInstructions }} »</p>
        </div>
      </div>
    }
  </div>
} @else {
  <div class="p-6 text-center text-xs text-gray-10 italic">
    Aucune règle de mandat configurée pour le moment.
  </div>
}
